const SVG_NS = 'http://www.w3.org/2000/svg';

let grassResizeFrame = 0;

function toSafeNumber(value, fallback, min, max) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) {
		return fallback;
	}
	return Math.max(min, Math.min(max, Math.floor(parsed)));
}

function toSafeFloat(value, fallback, min, max) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) {
		return fallback;
	}
	return Math.max(min, Math.min(max, parsed));
}

async function fetchGrassPaths(shape, limit) {
	const response = await fetch(
		`api.php?action=list&shape=${encodeURIComponent(shape)}&limit=${encodeURIComponent(String(limit))}`,
		{ cache: 'no-store' }
	);

	if (!response.ok) {
		return [];
	}

	const payload = await response.json();
	if (!payload?.ok || !Array.isArray(payload.items)) {
		return [];
	}

	return payload.items
		.map((item) => (typeof item?.path === 'string' ? item.path.trim() : ''))
		.filter((path) => path.length > 0);
}

function randomBetween(min, max) {
	return min + (Math.random() * (max - min));
}

function createTilePositions(viewportSize, repeatStep) {
	const count = Math.max(1, Math.ceil(viewportSize / repeatStep));
	const positions = [];
	for (let index = 0; index < count; index += 1) {
		positions.push(index * repeatStep);
	}
	return positions;
}

function createBladeTransform(tileSize, safeZone, bladeScale, overlapScale) {
	const minPos = safeZone;
	const maxPos = Math.max(minPos, tileSize - safeZone);
	const x = randomBetween(minPos, maxPos);
	const y = randomBetween(minPos, maxPos);
	const scale = bladeScale * overlapScale * randomBetween(0.9, 1.25);
	const rotate = randomBetween(-20, 20);

	return `translate(${x.toFixed(2)} ${y.toFixed(2)}) scale(${scale.toFixed(4)}) rotate(${rotate.toFixed(2)})`;
}

function createGrassTile(paths, tileSize, bladeCount, safeZone, bladeScale, overlapScale, x, y) {
	const tile = document.createElementNS(SVG_NS, 'svg');
	tile.classList.add('grass-field__tile');
	tile.setAttribute('viewBox', `0 0 ${tileSize} ${tileSize}`);
	tile.setAttribute('width', String(tileSize));
	tile.setAttribute('height', String(tileSize));
	tile.setAttribute('aria-hidden', 'true');
	tile.setAttribute('focusable', 'false');
	tile.style.left = `${x}px`;
	tile.style.top = `${y}px`;

	for (let index = 0; index < bladeCount; index += 1) {
		const bladeWrap = document.createElementNS(SVG_NS, 'g');
		bladeWrap.classList.add('grass-field__blade-wrap');
		bladeWrap.setAttribute('transform', createBladeTransform(tileSize, safeZone, bladeScale, overlapScale));

		const blade = document.createElementNS(SVG_NS, 'path');
		blade.classList.add('grass-field__blade');
		blade.setAttribute('d', paths[index % paths.length]);

		bladeWrap.appendChild(blade);
		tile.appendChild(bladeWrap);
	}

	return tile;
}

function renderGrassTiles(root, paths, tileSize, bladeCount, safeZone, repeatStep, bladeScale, overlapScale) {
	root.replaceChildren();

	const xPositions = createTilePositions(window.innerWidth, repeatStep);
	const yPositions = createTilePositions(window.innerHeight, repeatStep);

	for (const x of xPositions) {
		for (const y of yPositions) {
			root.appendChild(
				createGrassTile(paths, tileSize, bladeCount, safeZone, bladeScale, overlapScale, x, y)
			);
		}
	}
}

export async function initGrassField() {
	const root = document.getElementById('grassField');
	if (!root) {
		return;
	}

	const shape = root.getAttribute('data-grass-shape') || 'grass';
	const tileSize = toSafeNumber(root.getAttribute('data-grass-tile-size'), 600, 100, 2000);
	const bladeCount = toSafeNumber(root.getAttribute('data-grass-blade-count'), 320, 1, 2000);
	const safeZone = toSafeNumber(
		root.getAttribute('data-grass-safe-zone'),
		Math.round(tileSize * 0.12),
		0,
		Math.max(0, Math.floor(tileSize / 2) - 1)
	);
	const repeatStep = toSafeNumber(root.getAttribute('data-grass-repeat-step'), 500, 100, tileSize);
	const bladeScale = toSafeFloat(root.getAttribute('data-grass-blade-scale'), 0.24, 0.01, 2);
	const overlapScale = toSafeFloat(root.getAttribute('data-grass-overlap-scale'), 1.14, 0.1, 3);

	const paths = await fetchGrassPaths(shape, bladeCount);
	if (paths.length === 0) {
		root.style.display = 'none';
		return;
	}
	root.style.display = '';

	const render = () => {
		renderGrassTiles(root, paths, tileSize, bladeCount, safeZone, repeatStep, bladeScale, overlapScale);
	};

	render();

	if (root._grassResizeHandler) {
		window.removeEventListener('resize', root._grassResizeHandler);
	}

	const handleResize = () => {
		if (grassResizeFrame) {
			window.cancelAnimationFrame(grassResizeFrame);
		}
		grassResizeFrame = window.requestAnimationFrame(() => {
			grassResizeFrame = 0;
			render();
		});
	};

	root._grassResizeHandler = handleResize;
	window.addEventListener('resize', handleResize);
}
